import React from 'react'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../../../state'
import {
  handleFormNumber,
  useMeasurementMethodSelect,
  useRestaurantListField,
  useTaskForm,
  useTaskGroupSelect,
  valueExists
} from '../../../config/utils'
import {HygieneTaskResponse} from '../../../state/rest'
import {Button, ButtonRowWrap, ButtonRowGrid} from '../../Atoms/Buttons'
import {BigLabelInput, LabelInput, Select, Toggler} from '../../Atoms/Forms'
import {Baseline, Grid, InvisibleContainer, Row, GridSpan} from '../../Layout/Grid'
import {MainLayoutWithoutStretch, RightSidebar, MainContent} from '../../Layout/Layout'
import LocationsList from '../../Organisms/Locations/LocationsList'
import TaskScheduling from '../../Organisms/Scheduler/TaskScheduling'
import {DefaultTagsWidget} from '../../Organisms/Tags/DefaultSuggestionWidget'
import {SmallUnit, P} from '../../Atoms/Typography'
import {HeaderRow, HeaderActions} from '../../Molecules/ViewComponents/ViewHeader'
import TaskMenuModal from './TaskMenuModal'
import {CommonInformationFields, TasksGoBackHeaderTitle} from './CommonTasksComponents'
import IsUserHealthInspector from '../../../hooks/userIsHealthInspector'

interface FormProps {
  savedData?: HygieneTaskResponse
}
const TASK_KEY = 'hygiene'

const CreateHygieneTask = ({savedData}: FormProps) => {
  const {t} = useTranslation('tasks')
  const {state} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()

  const isEditing = !!savedData && !!savedData.id
  const isDuplicate = !!savedData && !savedData.id

  const saved = savedData
    ? isDuplicate && savedData.name
      ? {
          ...savedData,
          name: `${t('tasks:labels.copyOf', 'Copy of')} ${savedData.name.toLowerCase()}`,
          sites: [state.site!]
        }
      : savedData
    : undefined

  // task form controller hook
  const fc = useTaskForm(TASK_KEY, saved)
  const rc = useRestaurantListField(
    {watch: fc.watch, setValue: fc.setValue},
    isDuplicate ? undefined : saved ? saved.sites : undefined
  )

  const {taskGroupOptions, selectTaskGroup, selectedTaskGroup} = useTaskGroupSelect(
    TASK_KEY,
    saved ? saved.taskGroup : undefined
  )
  const {selectMethod, selectedMeasurementMethod, measurementOptions} = useMeasurementMethodSelect(
    'device',
    saved ? saved.measurementMethod : undefined
  )

  const {maxValue, unit, target} = fc.form

  const taskPayload = {
    maxValue: handleFormNumber(maxValue),
    defaultSuggestions: fc.defaultSuggestions,
    measurementMethod: selectedMeasurementMethod,
    taskGroup: selectedTaskGroup,
    unit: unit || 'RLU',
    target,
    schedule: fc.scheduledPayload,
    sites: rc.list,
    assets: fc.assets,
    lateByDays: fc.lateByDays
  }

  const isFormValid = () => {
    const isNameValid = !!fc.form.name
    const isMaxValueValid = valueExists(fc.form.maxValue)
    return isNameValid && isMaxValueValid
  }

  return (
    <MainLayoutWithoutStretch>
      <MainContent variant="white">
        <HeaderRow
          modal={
            isEditing && (
              <TaskMenuModal
                isOpen={fc.modalOpen}
                onClose={() => fc.setModalOpen(false)}
                onDelete={fc.editingDisabled ? undefined : fc.remove}
                onDuplicate={fc.duplicate}
              />
            )
          }
        >
          <TasksGoBackHeaderTitle
            label={
              isEditing
                ? t('tasks:labels.editTask.hygiene', 'Edit surface hygiene task')
                : t('tasks:labels.newTask.hygiene', 'Add surface hygiene task')
            }
            isEditing={isEditing}
          />
          {isEditing && <HeaderActions onMenu={() => fc.setModalOpen(true)} />}
        </HeaderRow>
        <InvisibleContainer>
          <Baseline>
            <Grid>
              <BigLabelInput
                required
                placeholder={t('tasks:placeholders.newTaskName', 'New task name')}
                name="name"
                id="name"
                labelText={t('tasks:labels.taskName', 'Task name')}
                ref={fc.register({
                  required: `${t('common:validation.nameRequired', 'Name field is required')}`
                })}
                errorMsg={!!fc.errors.name ? fc.errors.name!.message : undefined}
              ></BigLabelInput>
              <div className="empty"></div>
            </Grid>
            <Grid>
              <Baseline>
                <LabelInput
                  labelText={t('tasks:completed.measurementTarget', 'Measurement target')}
                  ref={fc.register}
                  name="target"
                  id="target"
                />

                <Select
                  returnId
                  nativeProps={{
                    name: 'taskGroup',
                    onChange(e) {
                      selectTaskGroup(e.target.value)
                    },
                    value: selectedTaskGroup.id
                  }}
                  id="taskGroup"
                  label={t('tasks:labels.taskGroup', 'Task group')}
                  options={taskGroupOptions}
                />

                <Select
                  returnId
                  nativeProps={{
                    name: 'measurementMethod',
                    onChange(e) {
                      selectMethod(measurementOptions.find(mo => mo.id === e.target.value)!.id)
                    },
                    value: selectedMeasurementMethod.id
                  }}
                  id="measurementMethod"
                  label={t('tasks:labels.measurementMethod', 'Measurement method')}
                  options={measurementOptions}
                />

                {selectedMeasurementMethod.id === 'manual' ? (
                  <LabelInput
                    name="unit"
                    id="unit"
                    ref={fc.register}
                    labelText={t('tasks:labels.measurementUnit', 'Measurement unit')}
                  />
                ) : null}

                <Toggler
                  label={t('tasks:labels.alarms', 'Alarms')}
                  values={[t('common:togglerValues.off', 'Off'), t('common:togglerValues.on', 'On')]}
                  initiallyChecked={fc.showAlarm}
                  name="isAlarms"
                  onClick={() => {
                    fc.setShowAlarm(!fc.showAlarm)
                    fc.reset()
                    fc.triggerValidation()
                  }}
                />

                {fc.showAlarm && (
                  <Row childMargin="1rem">
                    <LabelInput
                      required
                      type="number"
                      ref={r => fc.register({required: fc.showAlarm})(r)}
                      name="maxValue"
                      labelText={t('tasks:labels.alarmLimit', 'Alarm limit')}
                      errorMsg={
                        !!fc.errors.maxValue
                          ? t('common:validation.invalidValue', 'Invalid or missing value')
                          : undefined
                      }
                    />
                    <SmallUnit>{unit || 'RLU'}</SmallUnit>
                  </Row>
                )}

                <TaskScheduling
                  enabled={fc.schedulingEnabled}
                  frequency={fc.selectedFrequency}
                  incomplete={fc.scheduleIncomplete}
                  onToggle={fc.toggleShow}
                  onFrequencyChange={fc.setSelectedFrequency}
                />
              </Baseline>
              <Baseline>
                <CommonInformationFields useForm={fc} />
                <Baseline>
                  <DefaultTagsWidget
                    onChange={items => fc.setDefaultSuggestions(items)}
                    savedItems={saved ? saved.defaultSuggestions : undefined}
                  />
                </Baseline>
              </Baseline>
            </Grid>
            <ButtonRowWrap>
              <div className="empty"></div>
              <ButtonRowGrid>
                <Button name="cancel-button" negative variant="secondary" disabled={fc.loading} onClick={fc.cancel}>
                  {t('common:buttons.cancel', 'Cancel')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={
                    fc.scheduleIncomplete ||
                    !isFormValid() ||
                    fc.loading ||
                    rc.list.length === 0 ||
                    fc.editingDisabled ||
                    isHealthInspector
                  }
                  onClick={() => (isEditing ? fc.update(taskPayload) : fc.submit(taskPayload))}
                >
                  {t('common:buttons.save', 'Save')}
                </Button>

                {fc.editingDisabled && (
                  <GridSpan span={2}>
                    <P>
                      {t(
                        'tasks:messages.taskInUseMessage',
                        'This task is in use for locations you don’t have access to. You can not edit or delete it but you can duplicate the task to create a new one'
                      )}
                    </P>
                  </GridSpan>
                )}
              </ButtonRowGrid>
            </ButtonRowWrap>
          </Baseline>
        </InvisibleContainer>
      </MainContent>
      <RightSidebar>
        <LocationsList
          controller={rc}
          cyKey={'task'}
          label={t('tasks:labels.selectLocation', 'Select which locations use this task')}
        />
      </RightSidebar>
    </MainLayoutWithoutStretch>
  )
}

export default CreateHygieneTask
